import React, { useCallback, useState } from 'react';
import Table from "../Table";
import Image from "../Image";
import Modal from "../Modal";
import ProductWizard from "./Product/ProductWizard";

// Product image cell
function ProductImageCell({ image, name }) {
    // Memoize handlers so they are stable between renders
    const handleImageLoad = useCallback(() => {
        console.log(`Image loaded: ${name}`);
    }, [name]);
    const handleImageError = useCallback(() => {
        console.error(`Failed to load image: ${name}`);
    }, [name]);
    if (image && image.cdnUrl) {
        return <Image
            image={image}
            width={80}
            height={50}
            alt={name}
            className="product-image"
            lazy={true}
            fit="cover"
            quality={80}
            cacheBuster={false}
            onLoad={handleImageLoad}
            onError={handleImageError}
            style={{margin: 'auto', display: 'block'}}
        />
    }
    return <span>&nbsp;</span>;
}

function formatPrice(cents) {
    return `$${(Number(cents) / 100).toFixed(2)}`;
}

function formatPayWhatYouWantRange(payWhatYouWant) {
    const minCents = payWhatYouWant.minCents ?? 0;
    if (payWhatYouWant.maxCents == null) {
        return `${formatPrice(minCents)}+`;
    }
    return `${formatPrice(minCents)}–${formatPrice(payWhatYouWant.maxCents)}`;
}

// Product name/type/price cell
function ProductNameCell({ uuid, name, type, price, settings }) {
    const itemType = type || 'unknown';
    const capitalizedItemType = itemType.charAt(0).toUpperCase() + itemType.slice(1);
    const payWhatYouWant = settings?.payWhatYouWant;
    const isPayWhatYouWant = payWhatYouWant?.enabled === true;
    return (
        <div>
            <a href={`/account/products/${uuid}`}><strong>{name} <i className="fa-solid fa-edit"></i></strong></a><br/>
            <small>
                {capitalizedItemType} &bull; {isPayWhatYouWant
                    ? 'Pay what you want'
                    : formatPrice(price)}
            </small>
            {isPayWhatYouWant && (
                <>
                    <br/>
                    <small>
                        {formatPayWhatYouWantRange(payWhatYouWant)}
                        {' '}&bull; Recommended {formatPrice(price)}
                    </small>
                </>
            )}
        </div>
    );
}

// Orders cell
function ProductOrdersCell({ paidOrdersCount, unpaidOrdersCount, totalOrdersCount }) {
    if (totalOrdersCount) {
        return <span>{paidOrdersCount} paid, {unpaidOrdersCount} unpaid</span>;
    }
    return <span>0 orders</span>;
}

// Status cell
function ProductStatusCell({ isAvailable, channelUrl, uuid }) {
    if (isAvailable) {
        return (
            <span>
                    <span className="success">Active</span><br/>
                    <a href={`${channelUrl}/store/detail/${uuid}`} target="_blank" rel="noopener noreferrer">
                        <small>View in Store <i className="fa-solid fa-arrow-up-right-from-square"></i></small>
                    </a>
                </span>
        );
    }
    return <span className="error">Draft</span>;
}

// Move cell with drag for desktop and up/down links for mobile
function ProductMoveCell({ uuid, onMoveSuccess, onMoveError }) {
    const handleMove = async (direction) => {
        try {
            const response = await fetch(`/account/products/${uuid}/move/${direction}`, {
                method: 'GET',
                headers: {
                    'Accept': 'application/json'
                }
            });
            
            if (response.ok) {
                // Reload the page to reflect the new order
                window.location.reload();
            } else {
                throw new Error(`Failed to move product ${direction}`);
            }
        } catch (error) {
            console.error(`Error moving product ${direction}:`, error);
            if (onMoveError) {
                onMoveError(`Cannot move product ${direction}. Please try again.`);
            }
        }
    };

    return (
        <div>
            <div className="hide-mobile cursor-move">
                Drag to Move <i className="fa-solid fa-grip-lines"></i>
            </div>
            <div className="show-mobile">
                <a 
                    href="#"
                    onClick={(e) => { e.preventDefault(); handleMove('up'); }}
                    style={{marginRight: '1em'}}
                >
                    <i className="fa-solid fa-arrow-up"></i> Up
                </a>
                <a 
                    href="#"
                    onClick={(e) => { e.preventDefault(); handleMove('down'); }}
                    style={{float: 'right'}}
                >
                    Down <i className="fa-solid fa-arrow-down"></i>
                </a>
            </div>
        </div>
    );
}

export default function ProductsTable({
                                          products, setProducts,
                                          success, setSuccess,
                                          error, setError,
                                          channel,
                                          onSelectionChange,
                                          enableWizard = false,
                                          onProductsChanged,
                                            }) {
    const [wizardProductUuid, setWizardProductUuid] = useState(null);

    // Handle drag end
    const onDragEnd = async (result) => {
        setProducts(result);
        // Build mapping for server
        const mapping = {};
        [...result].reverse().forEach((item, idx) => {
            mapping[item.uuid] = idx + 1;
        });
        try {
            const response = await fetch('/account/products/reorg', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(mapping)
            });
            if (!response.ok) {
                // Optionally show error UI
                throw new Error('Failed to reorganize products');
            }
            setSuccess('Products reorganized successfully!');
        } catch (e) {
            console.error('Error reorganizing products:', e);
            setError('Failed to reorganize products. Please try again.');
        }
    };

    const COLS = [
        {
            key: 'image',
            label: 'Image',
            header: <span></span>,
            render: (value, row) => <ProductImageCell image={row.image} name={row.name} />
        },
        {
            key: 'name',
            header: 'Name',
            render: (value, row) => (
                <ProductNameCell
                    uuid={row.uuid}
                    name={row.name}
                    type={row.type}
                    price={row.price}
                    settings={row.settings}
                />
            )
        },
        {
            key: 'orders',
            header: 'Orders',
            render: (value, row) => <ProductOrdersCell paidOrdersCount={row.paidOrdersCount} unpaidOrdersCount={row.unpaidOrdersCount} totalOrdersCount={row.totalOrdersCount} />
        },
        {
            key: 'status',
            header: 'Status',
            render: (value, row) => <ProductStatusCell isAvailable={row.isAvailable} channelUrl={channel?.url} uuid={row.uuid} />
        },
        {
            key: 'move',
            header: 'Move',
            render: (value, row) => (
                <ProductMoveCell
                    uuid={row.uuid}
                    onMoveSuccess={setSuccess}
                    onMoveError={setError}
                />
            )
        },
        ...(enableWizard ? [{
            key: 'wizard',
            header: 'Wizard',
            render: (value, row) => (
                <button
                    type="button"
                    className="button"
                    style={{fontSize: '0.8rem', padding: '0.3rem 0.6rem'}}
                    onClick={() => setWizardProductUuid(row.uuid)}
                >
                    <i className="fa-solid fa-wand-magic-sparkles"></i> Edit
                </button>
            )
        }] : []),
    ];

    const handleWizardComplete = (updatedProduct) => {
        setWizardProductUuid(null);
        setSuccess('Product updated successfully!');
        onProductsChanged?.();
    };

    const handleWizardClose = () => {
        setWizardProductUuid(null);
        onProductsChanged?.();
    };

    return (
        <>
            <Table
                data={products}
                columns={COLS}
                keyField="uuid"
                draggable={true}
                onDragEnd={onDragEnd}
                selectable={true}
                onSelectionChange={onSelectionChange}
            />
            {enableWizard && (
                <Modal
                    isOpen={!!wizardProductUuid}
                    onClose={handleWizardClose}
                    title="Edit Product"
                    size="large"
                    closeOnOverlayClick={false}
                >
                    {wizardProductUuid && (
                        <ProductWizard
                            productUuid={wizardProductUuid}
                            modal={true}
                            onComplete={handleWizardComplete}
                            onClose={handleWizardClose}
                        />
                    )}
                </Modal>
            )}
        </>
    );

}
